import ExampleTheme from "../../themes/ExampleTheme";
import { $createParagraphNode, $createTextNode, $getRoot, $insertNodes } from "lexical";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import ToolbarPlugin from "../../plugins/ToolbarPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { useEffect, useState } from "react";
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import ListMaxIndentLevelPlugin from "../../plugins/ListMaxIndentLevelPlugin";
import CodeHighlightPlugin from "../../plugins/CodeHighlightPlugin";
import AutoLinkPlugin from "../../plugins/AutoLinkPlugin";
import NodeReferencePlugin from "../../plugins/NodeReferencePlugin";
import { NodeReferenceNode } from "../../plugins/nodes/NodeReferenceNode";
import { Button, ButtonGroup} from '@mui/material';
import { ImageNode } from "../../plugins/ImagePlugin";
import "../../../editorStyles.css";

// CoreForum UpdatePlugin - 給子模式繼承使用
function CoreForumUpdatePlugin({ btns }) {
  const [editor] = useLexicalComposerContext();
  function update(text) {
    editor.update(() => {
      const root = $getRoot();
      const p = $createParagraphNode();
      p.append($createTextNode(text));
      root.append(p);
    });
  }
  const saffoldBody = btns?.saffoldBody || [];
  if (!saffoldBody || saffoldBody.length === 0) {
    return null;
  }
  return <>
    {
      saffoldBody.map((text, index) => {
        return <Button
          key={index}
          onClick={() => {
            update(text);
          }}
          variant="outlined"
          fullWidth
          sx={{
            textTransform: 'none',
            justifyContent: 'flex-start',
            textAlign: 'left',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            borderRadius: '8px',
            borderColor: '#e2e8f0',
            color: '#475569',
            padding: '10px 14px',
            fontSize: '0.875rem',
            lineHeight: 1.4,
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#3b82f6',
              backgroundColor: '#eff6ff',
              color: '#2563eb',
              transform: 'translateX(4px)'
            }
          }}
        >
          {text}
        </Button>;
      })
    }
  </>
};

// 可由子模式覆寫的 Placeholder
function Placeholder({ text = "在這裡輸入你的想法..." }) {
  return <div className="editor-placeholder">{text}</div>;
}

const HtmlPlugin = ({ initialHtml, onHtmlChanged }) => {
  const [editor] = useLexicalComposerContext();

  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
      if (!initialHtml || !isFirstRender) return;

      setIsFirstRender(false);

      editor.update(() => {
          const parser = new DOMParser();
          const dom = parser.parseFromString(initialHtml, "text/html");
          const nodes = $generateNodesFromDOM(editor, dom);
          $insertNodes(nodes);
      });
  }, []);

  return (
      <OnChangePlugin
          onChange={(editorState) => {
              editorState.read(() => {
                  onHtmlChanged($generateHtmlFromNodes(editor));
              });
          }}
      />
  );
};

const editorConfig = {
  // The editor theme
  theme: ExampleTheme,
  // Handling of errors during update
  onError(error) {
    throw error;
  },
  // Any custom nodes go here
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    CodeHighlightNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode,
    ImageNode,
    NodeReferenceNode
  ]
};


// CoreForum 預設的 scaffold 內容 - 子模式可覆寫
const DEFAULT_CORE_FORUM_SCAFFOLD = {
  saffoldBody: [
    "💡這邊我想提供一個我的想法",
    "🧐我覺得這邊應該要",
    "❓我想知道",
    "🙅🏻這不能解釋",
    "📄能針對 舉例嗎",
    "📄能針對 提供參考資料嗎",
    "✍🏻我到目前為止的想法是"
  ],
  scaffoldWords: [
    "這邊我想提供一個我的想法", 
    "我覺得這邊應該要", 
    "我想知道", 
    "這不能解釋", 
    "能針對 舉例嗎",
    "能針對 提供參考資料嗎", 
    "我到目前為止的想法是"
  ]
};

export function CoreForumEditor({ changeFunc, nodePlan, placeholderText, graphNodes}) {
  // 使用傳入的 nodePlan 或預設的 CoreForum scaffold
  const effectiveNodePlan = nodePlan || DEFAULT_CORE_FORUM_SCAFFOLD;

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        width: '100%',
        alignItems: 'flex-start'
      }}>
        {effectiveNodePlan?.saffoldBody && effectiveNodePlan.saffoldBody.length > 0 && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            minWidth: '180px',
            maxWidth: '200px',
            flexShrink: 0
          }}>
            <CoreForumUpdatePlugin
              btns={effectiveNodePlan} 
            />
          </div>
        )}
        <div className="editor-container" style={{
          flex: 1,
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          transition: 'border-color 0.2s, box-shadow 0.2s'
        }}>
          <ToolbarPlugin />
          <div className="editor-inner" style={{
            padding: '16px',
            minHeight: '200px'
          }}>
            <RichTextPlugin
              contentEditable={<ContentEditable className="editor-input" />}
              placeholder={<Placeholder text={placeholderText} />}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            <AutoFocusPlugin />
            <CodeHighlightPlugin />
            <ListPlugin />
            <LinkPlugin />
            <AutoLinkPlugin />
            <ListMaxIndentLevelPlugin maxDepth={7} />
            <NodeReferencePlugin graphNodes={graphNodes} />

            <HtmlPlugin
              onHtmlChanged={(html) => changeFunc(html)}
              initialHtml={effectiveNodePlan.intro || ""}
            />
          </div>
        </div>
      </div>
    </LexicalComposer>
  );
}